import { Anchor, Box, Group, Text } from '@mantine/core';
import { useMutation } from '@tanstack/react-query';

import { mutateJson } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { showToast } from '../../lib/toast';

type ResendResponse = { ok: boolean; alreadyVerified: boolean };

/**
 * EmailVerificationBanner — top-of-shell banner shown only when the signed-in
 * user is in `status='pending'`. Mirrors `ClosingBanner`'s shape. Reachable
 * only when the `email_verification` platform flag is on — sign-up otherwise
 * inserts `status='active'` directly, so this banner is dormant by default.
 */
export function EmailVerificationBanner() {
  const { user, refresh } = useAuth();

  const resend = useMutation({
    mutationFn: async () => mutateJson<ResendResponse>('/api/auth/resend-verification'),
    onSuccess: async (resp) => {
      showToast({
        kind: 'success',
        body: resp.alreadyVerified
          ? 'Your email is already confirmed.'
          : 'Sent. Check your inbox for the confirmation link.',
      });
      if (resp.alreadyVerified) await refresh();
    },
    onError: (err: unknown) => {
      showToast({
        kind: 'error',
        body: err instanceof Error ? err.message : 'Could not resend the confirmation link.',
      });
    },
  });

  if (user === null) return null;
  if (user.status !== 'pending') return null;

  return (
    <Box
      style={{
        background: 'light-dark(var(--mantine-color-sun-0), var(--mantine-color-dark-5))',
        borderBottom: '1px solid var(--mantine-color-sun-3)',
        paddingBlock: 10,
        paddingInline: 16,
      }}
    >
      <Group justify="center" gap="md" wrap="wrap">
        <Text size="sm" c="ink.8">
          Confirm your email to unlock every part of WWAAVE. Check your inbox for the link.
        </Text>
        <Anchor
          component="button"
          type="button"
          size="sm"
          c="brand.7"
          fw={600}
          disabled={resend.isPending}
          onClick={() => resend.mutate()}
        >
          Resend the link
        </Anchor>
      </Group>
    </Box>
  );
}
